/**
 * Design System - Modal Component
 *
 * Accessible modal dialog with backdrop, animations, and focus trapping.
 *
 * @example
 * <Modal
 *   isOpen={isOpen}
 *   onClose={handleClose}
 *   title="Confirm Action"
 * >
 *   <p>Are you sure you want to proceed?</p>
 * </Modal>
 *
 * @example
 * <Modal
 *   isOpen={isOpen}
 *   onClose={handleClose}
 *   size="lg"
 *   showCloseButton={false}
 * >
 *   <Modal.Header>
 *     <h2>Custom Header</h2>
 *   </Modal.Header>
 *   <Modal.Body>
 *     Content here
 *   </Modal.Body>
 *   <Modal.Footer>
 *     <Button onClick={handleClose}>Cancel</Button>
 *     <Button variant="primary">Confirm</Button>
 *   </Modal.Footer>
 * </Modal>
 */

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { cn } from '../utils/classNames';
import { FiX } from 'react-icons/fi';

const Modal = ({
  children,
  isOpen = false,
  onClose,
  title,
  size = 'md',
  showCloseButton = true,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  className,
  ...props
}) => {
  const modalRef = useRef(null);
  const previousActiveElement = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  // Manage visibility state for exit animation
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
    }
  }, [isOpen]);

  // Size variants
  const sizeStyles = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
    '4xl': 'max-w-4xl',
    full: 'max-w-full mx-4',
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (closeOnEscape && e.key === 'Escape' && isOpen) {
        onClose?.();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, closeOnEscape, onClose]);

  // Focus management
  useEffect(() => {
    if (isOpen) {
      // Save current focus
      previousActiveElement.current = document.activeElement;

      // Focus modal
      modalRef.current?.focus();

      // Prevent body scroll
      document.body.style.overflow = 'hidden';
    } else {
      // Restore body scroll
      document.body.style.overflow = '';

      // Restore focus
      previousActiveElement.current?.focus();
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Focus trap
  useEffect(() => {
    if (!isOpen) return;

    const modal = modalRef.current;
    if (!modal) return;

    const focusableElements = modal.querySelectorAll(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTab = (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    modal.addEventListener('keydown', handleTab);
    return () => modal.removeEventListener('keydown', handleTab);
  }, [isOpen]);

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (closeOnBackdropClick && e.target === e.currentTarget) {
      onClose?.();
    }
  };

  const modalContent = (
    <>
      {isVisible && (
        <div
          className={cn(
            'fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50',
            isOpen ? 'animate-fade-in' : 'animate-fade-out'
          )}
          onClick={handleBackdropClick}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? 'modal-title' : undefined}
          onAnimationEnd={() => {
            if (!isOpen) {
              setIsVisible(false);
            }
          }}
        >
          <div
            ref={modalRef}
            className={cn(
              'relative bg-white rounded-xl shadow-2xl w-full',
              sizeStyles[size],
              isOpen ? 'animate-scale-in' : 'animate-scale-out',
              className
            )}
            tabIndex={-1}
            {...props}
          >
        {/* Close button */}
        {showCloseButton && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
            aria-label="Cerrar modal"
          >
            <FiX className="h-5 w-5" aria-hidden="true" />
          </button>
        )}

        {/* Simple layout with title */}
        {title && !React.Children.toArray(children).some(child => child?.type?.displayName?.includes('Modal')) ? (
          <>
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 id="modal-title" className="text-xl font-semibold text-gray-900">
                {title}
              </h2>
            </div>
            <div className="px-6 py-4">
              {children}
            </div>
          </>
        ) : (
          children
        )}
          </div>
        </div>
      )}
    </>
  );

  return createPortal(modalContent, document.body);
};

Modal.propTypes = {
  children: PropTypes.node.isRequired,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  title: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl', 'full']),
  showCloseButton: PropTypes.bool,
  closeOnBackdropClick: PropTypes.bool,
  closeOnEscape: PropTypes.bool,
  className: PropTypes.string,
};

// Modal Header subcomponent
const ModalHeader = ({ children, className, ...props }) => (
  <div
    className={cn('px-6 py-4 border-b border-gray-200', className)}
    {...props}
  >
    {children}
  </div>
);

ModalHeader.displayName = 'Modal.Header';
ModalHeader.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

// Modal Body subcomponent
const ModalBody = ({ children, className, ...props }) => (
  <div className={cn('px-6 py-4', className)} {...props}>
    {children}
  </div>
);

ModalBody.displayName = 'Modal.Body';
ModalBody.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

// Modal Footer subcomponent
const ModalFooter = ({ children, className, ...props }) => (
  <div
    className={cn('px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-xl flex justify-end gap-3', className)}
    {...props}
  >
    {children}
  </div>
);

ModalFooter.displayName = 'Modal.Footer';
ModalFooter.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

// Attach subcomponents
Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;

export default Modal;
